import { ExceptionSerializer } from '@maia/exceptions';
import { MockLogger } from '../implementation/mock-logger';
import { SentryLogger } from '../implementation/sentry-logger';
import { ErrorLogger } from './logger.interface';
import { SeverityLevel } from '@sentry/react';

export class Logger {
  private static instance: ErrorLogger | null = null;

  static init(config: { dsn: string; environment: string; useMock?: boolean }) {
    const logger = config.useMock ? new MockLogger() : new SentryLogger();

    logger.init(config);
    this.instance = logger;
  }

  private static getInstance(): ErrorLogger {
    if (!this.instance) {
      throw new Error('[Logger] init() must be called before using Logger.');
    }
    return this.instance;
  }

  static captureMessage(message: string, level: SeverityLevel) {
    this.getInstance().captureMessage(message, level);
  }

  static captureException(error: unknown, level: SeverityLevel) {
    if (ExceptionSerializer.isSerializableError(error)) {
      const { exception, context } = ExceptionSerializer.toSentryPayload(
        error,
        level,
      );
      this.getInstance().captureException(exception, context);
    } else if (error instanceof Error) {
      this.getInstance().captureException(error, {
        level,
        extra: {
          message: error.message,
          stack: error.stack,
        },
      });
    } else {
      this.getInstance().captureException('Unknown error', {
        level,
        extra: { rawError: error },
      });
    }
  }

  static setUser(user: { guid: string }) {
    this.getInstance().setUser(user);
  }

  static setTag(key: string, value: string) {
    this.getInstance().setTag(key, value);
  }

  static setContext(name: string, context: Record<string, unknown>) {
    this.getInstance().setContext(name, context);
  }

  // Optional: swap logger during runtime (e.g. tests)
  static _swap(logger: ErrorLogger) {
    this.instance = logger;
  }
}
